import { Banner } from '@evisort/eds/components/Banner/Banner';
import { Button } from '@evisort/eds/components/Button/Button';
import { Divider } from '@evisort/eds/components/Divider/Divider';
import { FormField } from '@evisort/eds/components/FormField/FormField';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Text } from '@evisort/eds/components/Text/Text';
import { useForm, useStore } from '@tanstack/react-form';
import {
  type FormEvent,
  Suspense,
  lazy,
  memo,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { BRAND_NAME } from '~/common/constants/brand';
import { isDev } from '~/dev/utils/isDev';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import type { ForgotPasswordNavigationState } from './types';

import { LoginLayout } from './LoginLayout';
import styles from './LoginPage.module.css';
import { Account, LoginQuery, Sso, Stage } from './types';
import { testIsEmailValid } from './utils';

// Lazy so the heavy `@stytch/*` stack only loads when the rolodex/Stytch login
// UI is actually rendered, instead of shipping in the main entry chunk.
const StytchLoginForm = lazy(() => import('./StytchLoginForm'));

interface Props {
  /** Query when workspace is changed */
  queryChangeWorkspace: LoginQuery<(workspace: string) => void>;
  /** Query when logging in with account */
  queryLoginAccount: LoginQuery<(account: Account) => void>;
  /** Query when logging in with SSO provider */
  queryLoginSso: LoginQuery<(sso: Sso) => void>;
  /** Flag to determine if the user can use the rolodex and stytch login component */
  enableRolodex?: boolean;
  /** Flag to determine if the user should login through OIDC */
  enableOidc?: boolean;
  /** Callback when the user login successfully using the rolodex */
  onRolodexLoginComplete?: () => void;
  /** Callback to resolve login sso error */
  onResolveLoginSsoError: () => void;
  /** Specify a valid SSO provider */
  sso: Sso | null;
  /** Renders only SSO login elements */
  isSsoOnly?: boolean;
  /** Controlled stage */
  stage?: Stage;
  /** Controlled workspace */
  workspace?: string;
}

export const LoginPage = memo(
  ({
    isSsoOnly,
    queryChangeWorkspace,
    queryLoginAccount,
    queryLoginSso,
    enableRolodex = false,
    enableOidc = false,
    onRolodexLoginComplete,
    onResolveLoginSsoError,
    sso,
    stage: initialStage = 'workspace',
    workspace: initialWorkspace = '',
  }: Props) => {
    const intl = useIntl();
    const { location, navigate } = useRouting();
    const navigationState = location.state as
      | ForgotPasswordNavigationState
      | undefined;
    const [stage, setStage] = useState<Stage>(initialStage);

    const changeWorkspaceText = intl.formatMessage({
      id: 'LoginPage.changeWorkspaceButton',
      defaultMessage: 'Change Workspace',
      description: 'Button to change workspace',
    });

    const emailLabel = intl.formatMessage({
      id: 'LoginPage.emailLabel',
      defaultMessage: 'Email',
      description: 'Label for email input',
    });

    const emailPlaceholder = intl.formatMessage({
      id: 'LoginPage.emailPlaceholder',
      defaultMessage: 'Type email address',
      description: 'Placeholder for email input',
    });

    const signInLabel = intl.formatMessage({
      id: 'LoginPage.signInButton',
      defaultMessage: 'Sign In',
      description: 'Button to sign in',
    });

    const signInWithSsoText = intl.formatMessage(
      {
        id: 'LoginPage.signInWithSsoButton',
        defaultMessage: 'Sign In with {ssoName}',
        description:
          'Button to sign in with SSO provider. Example: ssoName = "Google"',
      },
      { ssoName: sso?.name ?? '' },
    );

    const forgotPasswordText = intl.formatMessage({
      id: 'LoginPage.forgotPasswordButton',
      defaultMessage: 'Forgot Your Password?',
      description: 'Button to reset password',
    });

    const continueText = intl.formatMessage({
      id: 'LoginPage.continueButton',
      defaultMessage: 'Continue',
      description: 'Button to continue after entering workspace',
    });

    const copyErrorText = intl.formatMessage({
      id: 'LoginPage.copyButton',
      defaultMessage: 'Copy',
      description: 'Copy button for error message',
    });

    const redirectingText = intl.formatMessage({
      id: 'LoginPage.redirectingToProvider',
      defaultMessage: 'Redirecting you to your sign-in provider…',
      description:
        'Message shown while the browser is handed off to an OIDC provider',
    });
    useEffect(() => setStage(initialStage), [initialStage]); // sync state from props

    const [onChangeWorkspace, { isFetching: isLoadingChangeWorkspace }] =
      queryChangeWorkspace;
    const [
      onLoginAccount,
      { isFetching: isLoadingLoginAccount, error: errorLoginAccount },
    ] = queryLoginAccount;
    const [
      onLoginSso,
      { error: errorLoginSso, isFetching: isLoadingLoginSso },
    ] = queryLoginSso;

    // The stage in scope when the form was built; submit dispatches on it.
    const isSubmitBlocked = isLoadingChangeWorkspace || isLoadingLoginAccount;

    const form = useForm({
      defaultValues: {
        workspace: initialWorkspace,
        email: navigationState?.email ?? '',
        password: '',
      },
      onSubmit: ({ value }) => {
        // Mirrors the primary button's disabled reason: a disabled button still
        // submits on Enter, so the keyboard path has to bail the same way.
        if (isSubmitBlocked) return;

        if (stage === 'login') {
          onLoginAccount({ email: value.email, password: value.password });
          return;
        }
        onChangeWorkspace(value.workspace);
      },
    });

    useFormFocusManager(form);

    const workspace = useStore(form.store, (state) => state.values.workspace);
    const submissionAttempts = useStore(
      form.store,
      (state) => state.submissionAttempts,
    );

    useEffect(() => {
      if (
        enableRolodex &&
        !enableOidc &&
        workspace &&
        stage === 'login' &&
        !window.location.pathname.includes(`${workspace}/login`)
      ) {
        window.history.replaceState(null, '', `${workspace}/login`);
      }
    }, [workspace, stage, enableRolodex, enableOidc]);

    const requiredFieldError = intl.formatMessage({
      id: 'LoginPage.requiredFieldError',
      defaultMessage: 'This field is required.',
      description: 'Validation error shown when a required field is empty',
    });

    const invalidEmailError = intl.formatMessage({
      id: 'LoginPage.invalidEmailError',
      defaultMessage: 'Enter a valid email address.',
      description: 'Validation error shown when the email address is malformed',
    });

    const validateRequired = ({ value }: { value: string }) =>
      value.trim() ? undefined : requiredFieldError;

    const validateEmail = ({ value }: { value: string }) => {
      if (!value.trim()) return requiredFieldError;
      return testIsEmailValid(value) ? undefined : invalidEmailError;
    };

    const resetLogin = () => {
      form.setFieldValue('email', '');
      form.setFieldValue('password', '');
    };

    let title;
    let description;
    let contents;
    switch (stage) {
      case 'login': {
        title = enableRolodex
          ? ''
          : intl.formatMessage({
              id: 'LoginPage.loginTitle',
              defaultMessage: 'Sign in to your Workspace',
              description: 'Title for sign in to workspace page',
            });
        description = enableRolodex ? null : (
          <>
            {intl.formatMessage({
              id: 'LoginPage.loginDescription',
              defaultMessage:
                'Enter your credentials to sign into the workspace: ',
              description: 'Prompt to enter workspace URL',
            })}
            <strong> {workspace}</strong>
          </>
        );
        if (enableRolodex) {
          if (enableOidc) {
            // OIDC redirects user to the OIDC provider to login
            contents = (
              <Text as="p" tone="subdued" variant="body-m">
                {redirectingText}
              </Text>
            );
          } else {
            contents = (
              <>
                <Suspense fallback={null}>
                  <StytchLoginForm
                    onRolodexLoginComplete={onRolodexLoginComplete}
                  />
                </Suspense>
                <Button
                  label={changeWorkspaceText}
                  text={changeWorkspaceText}
                  variant="tertiary"
                  onClick={() => {
                    resetLogin();
                    form.setFieldValue('workspace', '');
                    setStage('workspace');
                  }}
                />
              </>
            );
          }
        } else {
          contents = (
            <>
              {sso && (
                <Layout direction="column" spacing="m">
                  {errorLoginSso && (
                    <Banner
                      action={{
                        label: copyErrorText,
                        text: copyErrorText,
                        onClick: onResolveLoginSsoError,
                      }}
                      status="critical"
                      text={errorLoginSso}
                    />
                  )}
                  <Button
                    isLoading={isLoadingLoginSso}
                    label={signInWithSsoText}
                    text={signInWithSsoText}
                    variant="secondary"
                    onClick={() => {
                      resetLogin();
                      onLoginSso(sso);
                    }}
                  />
                  {isSsoOnly ? null : (
                    <Layout align="center" spacing="s">
                      <Layout flex="auto">
                        <Divider />
                      </Layout>
                      <Text tone="subdued" variant="body-s">
                        {intl.formatMessage({
                          id: 'LoginPage.orDivider',
                          defaultMessage: 'OR',
                          description: 'Divider label for alternative login',
                        })}
                      </Text>
                      <Layout flex="auto">
                        <Divider />
                      </Layout>
                    </Layout>
                  )}
                </Layout>
              )}
              {errorLoginAccount && (
                <Banner status="critical" text={errorLoginAccount} />
              )}
              {isSsoOnly ? null : (
                <>
                  <form.Field
                    name="email"
                    validators={{
                      onChange: validateEmail,
                      onSubmit: validateEmail,
                    }}
                  >
                    {(field) => (
                      <FormField
                        hint={
                          submissionAttempts > 0
                            ? (field.state.meta.errors[0] ?? undefined)
                            : undefined
                        }
                        label={emailLabel}
                        name={field.name}
                        placeholder={emailPlaceholder}
                        required
                        status={
                          submissionAttempts > 0 && field.state.meta.errors[0]
                            ? 'critical'
                            : undefined
                        }
                        type={isDev ? 'text' : 'email'}
                        value={field.state.value}
                        onChange={(next: string | null) =>
                          field.handleChange(next ?? '')
                        }
                      />
                    )}
                  </form.Field>
                  <form.Field
                    name="password"
                    validators={{
                      onChange: validateRequired,
                      onSubmit: validateRequired,
                    }}
                  >
                    {(field) => (
                      <FormField
                        hint={
                          submissionAttempts > 0
                            ? (field.state.meta.errors[0] ?? undefined)
                            : undefined
                        }
                        label={intl.formatMessage({
                          id: 'LoginPage.passwordLabel',
                          defaultMessage: 'Password',
                          description: 'Label for password input',
                        })}
                        name={field.name}
                        placeholder={intl.formatMessage({
                          id: 'LoginPage.passwordPlaceholder',
                          defaultMessage: 'Type password',
                          description: 'Placeholder for password input',
                        })}
                        required
                        status={
                          submissionAttempts > 0 && field.state.meta.errors[0]
                            ? 'critical'
                            : undefined
                        }
                        type="password"
                        value={field.state.value}
                        onChange={(next) => field.handleChange(next ?? '')}
                      />
                    )}
                  </form.Field>

                  <Button
                    isLoading={isSubmitBlocked}
                    label={signInLabel}
                    text={signInLabel}
                    type="submit"
                    variant="primary"
                  />
                  <Layout
                    align="center"
                    className={styles.inlineAction}
                    justify="center"
                    spacing="xs"
                  >
                    <Button
                      icon={{ key: 'arrow-left', position: 'left' }}
                      label={changeWorkspaceText}
                      size="s"
                      text={changeWorkspaceText}
                      variant="tertiary"
                      onClick={() => {
                        resetLogin();
                        form.setFieldValue('workspace', '');
                        setStage('workspace');
                      }}
                    />
                    <Button
                      label={forgotPasswordText}
                      size="s"
                      text={forgotPasswordText}
                      variant="tertiary"
                      onClick={() =>
                        navigate(RoutePathType.ForgotPassword, {
                          state: {
                            email: form.getFieldValue('email'),
                          } satisfies ForgotPasswordNavigationState,
                        })
                      }
                    />
                  </Layout>
                </>
              )}
            </>
          );
        }
        break;
      }
      case 'workspace':
      default: {
        title = intl.formatMessage({
          id: 'LoginPage.workspaceTitle',
          defaultMessage: 'Sign in to your Workspace',
          description: 'Title for sign in to workspace page',
        });
        description = intl.formatMessage(
          {
            id: 'LoginPage.workspaceDescription',
            defaultMessage: "Enter your workspace's {brandName} URL.",
            description: 'Prompt to enter workspace URL',
          },
          { brandName: BRAND_NAME },
        );
        contents = (
          <>
            <form.Field
              name="workspace"
              validators={{
                onChange: validateRequired,
                onSubmit: validateRequired,
              }}
            >
              {(field) => (
                <FormField
                  autoCapitalize="none"
                  hint={
                    submissionAttempts > 0 && field.state.meta.errors[0]
                      ? field.state.meta.errors[0]
                      : intl.formatMessage({
                          id: 'LoginPage.workspaceHint',
                          defaultMessage: 'clients.evisort.com/your-workspace',
                          description:
                            'Hint showing the shape of a workspace URL',
                        })
                  }
                  label={intl.formatMessage({
                    id: 'LoginPage.workspaceLabel',
                    defaultMessage: 'Workspace',
                    description: 'Label for workspace input',
                  })}
                  name={field.name}
                  placeholder={intl.formatMessage({
                    id: 'LoginPage.workspacePlaceholder',
                    defaultMessage: 'your-workspace',
                    description: 'Placeholder for workspace input',
                  })}
                  required
                  status={
                    submissionAttempts > 0 && field.state.meta.errors[0]
                      ? 'critical'
                      : undefined
                  }
                  type="text"
                  value={field.state.value}
                  onChange={(next) => field.handleChange(next ?? '')}
                />
              )}
            </form.Field>
            <Button
              isLoading={isLoadingChangeWorkspace}
              label={continueText}
              text={continueText}
              type="submit"
              variant="primary"
            />
          </>
        );
        break;
      }
    }

    return (
      <LoginLayout>
        <Layout
          aria-label={stage}
          action="#"
          as="form"
          className={styles.form}
          method="POST"
          direction="column"
          noValidate
          spacing="l"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            form.handleSubmit().catch(() => {
              // Errors surface through the query's own error state.
            });
          }}
        >
          <Layout
            key={stage}
            className={styles.stageContent}
            direction="column"
            spacing="l"
          >
            {(title || description) && (
              <Layout direction="column" spacing="xs">
                <Text as="h1" variant="heading-xs">
                  {title}
                </Text>
                {description && (
                  <Text as="p" tone="subdued" variant="body-m">
                    {description}
                  </Text>
                )}
              </Layout>
            )}
            {contents}
          </Layout>
        </Layout>
      </LoginLayout>
    );
  },
);
